import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { apiQuery } from 'store/query';
import { addParamsToUrl } from 'utils/query';
import { calculateOffset } from 'utils/helpers';

import {
  ReceiptSetting,
  CreateReceiptSetting,
  UpdateReceiptSetting,
  ReceiptSettingsRequest,
  ReceiptSettingsResponse,
} from './types';

const tagTypes = ['Receipt settings'];

export const receiptSettingsApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'receiptSettingsApi',
  endpoints: (build) => ({
    companySetting: build.query<ReceiptSetting, number>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: generatePath(URL.GET_RECEIPT_SETTING, { id }),
      }),
    }),

    create: build.mutation<ReceiptSetting, CreateReceiptSetting>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.GET_RECEIPT_SETTING,
      }),
    }),

    update: build.mutation<ReceiptSetting, UpdateReceiptSetting>({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'patch',
        url: URL.GET_RECEIPT_SETTINGS,
      }),
    }),

    list: build.query<ReceiptSettingsResponse, ReceiptSettingsRequest>({
      providesTags: tagTypes,
      query: (params) => {
        const { page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };

        return {
          method: 'get',
          url: addParamsToUrl(URL.GET_RECEIPT_SETTINGS, searchParams),
        };
      },
    }),
  }),
});

export const {
  useListQuery,
  useCreateMutation,
  useUpdateMutation,
  useCompanySettingQuery,
} = receiptSettingsApi;

export type { ReceiptSetting };
